import type React from 'react';
import { useCallback, useRef } from 'react';

interface UseBottomSheetDragOptions {
  sheetRef: React.RefObject<HTMLElement | null>;
  scrollRegionRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}

interface ActiveDrag {
  pointerId: number;
  startY: number;
  lastY: number;
  lastTime: number;
  velocityY: number;
}

const DRAG_CLOSE_DISTANCE = 150;
const DRAG_CLOSE_VELOCITY = 200;
const RETURN_TRANSITION = 'translate 220ms cubic-bezier(0.22, 0.61, 0.36, 1)';

export function useBottomSheetDrag({
  sheetRef,
  scrollRegionRef,
  onClose,
}: UseBottomSheetDragOptions) {
  const activeDragRef = useRef<ActiveDrag | null>(null);
  const returnTimerRef = useRef<number | null>(null);

  const handlePointerDownCapture = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const target = event.target instanceof Element ? event.target : null;
      if (
        !target?.closest('[data-sheet-drag-region="true"]') ||
        target.closest('button, input, select, textarea, a, [role="slider"]') ||
        scrollRegionRef.current?.scrollTop !== 0
      ) {
        return;
      }

      const sheet = sheetRef.current;
      if (!sheet) return;

      if (returnTimerRef.current !== null) {
        window.clearTimeout(returnTimerRef.current);
        returnTimerRef.current = null;
      }
      sheet.style.removeProperty('transition');
      activeDragRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        lastY: event.clientY,
        lastTime: event.timeStamp,
        velocityY: 0,
      };

      try {
        sheet.setPointerCapture(event.pointerId);
      } catch {
        activeDragRef.current = null;
      }
    },
    [scrollRegionRef, sheetRef],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = activeDragRef.current;
      const sheet = sheetRef.current;
      if (!drag || !sheet || drag.pointerId !== event.pointerId) return;

      const offset = Math.max(0, event.clientY - drag.startY);
      const elapsed = event.timeStamp - drag.lastTime;
      if (elapsed > 0) {
        drag.velocityY = ((event.clientY - drag.lastY) / elapsed) * 1000;
      }
      sheet.style.setProperty('translate', `0 ${offset}px`);
      drag.lastY = event.clientY;
      drag.lastTime = event.timeStamp;
    },
    [sheetRef],
  );

  const finishDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, canClose: boolean) => {
      const drag = activeDragRef.current;
      const sheet = sheetRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      activeDragRef.current = null;
      const offset = event.clientY - drag.startY;
      const finalDelta = event.clientY - drag.lastY;
      const elapsedSinceLastMove = Math.max(event.timeStamp - drag.lastTime, 0);
      const velocity =
        finalDelta !== 0
          ? (finalDelta / Math.max(elapsedSinceLastMove, 1)) * 1000
          : elapsedSinceLastMove <= 120
            ? drag.velocityY
            : 0;

      if (canClose && (offset > DRAG_CLOSE_DISTANCE || velocity > DRAG_CLOSE_VELOCITY)) {
        onClose();
        return;
      }

      if (!sheet) return;
      sheet.style.transition = RETURN_TRANSITION;
      sheet.style.setProperty('translate', '0 0');
      const cleanupTransition = (transitionEvent: TransitionEvent) => {
        if (transitionEvent.propertyName !== 'translate') return;
        sheet.style.removeProperty('transition');
        sheet.removeEventListener('transitionend', cleanupTransition);
        if (returnTimerRef.current !== null) {
          window.clearTimeout(returnTimerRef.current);
          returnTimerRef.current = null;
        }
      };
      sheet.addEventListener('transitionend', cleanupTransition);
      returnTimerRef.current = window.setTimeout(() => {
        sheet.style.removeProperty('transition');
        sheet.removeEventListener('transitionend', cleanupTransition);
        returnTimerRef.current = null;
      }, 280);
    },
    [onClose, sheetRef],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => finishDrag(event, true),
    [finishDrag],
  );

  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => finishDrag(event, false),
    [finishDrag],
  );

  return {
    onPointerDownCapture: handlePointerDownCapture,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
  };
}
